import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Entity, PermissionLevels } from '@debbie/common/dist/index.js';
import { convertUserToContact } from '@debbie/common/dist/cases/index.js';
import { getContactPerson } from '@debbie/common/dist/customers/index.js';
import { getFoundations, isDocumentFoundation, } from '@debbie/common/dist/documents/index.js';
import { isCaseworker } from '@debbie/common/dist/permissions/index.js';
import { UpdateType, } from '@debbie/common/dist/updates/index.js';
import { getNextTask } from '@debbie/common/dist/workflows/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { selectCaseUpdates } from '@debbie/cortex/dist/case/selectors.js';
import { selectCustomerById } from '@debbie/cortex/dist/customers/selectors.js';
import { selectRole } from '@debbie/cortex/dist/me/selectors.js';
import { selectUpdateTypesMap } from '@debbie/cortex/dist/update-types/selectors.js';
import { selectWorkflows, selectWorkflowsIsLoading, } from '@debbie/cortex/dist/workflows/selectors.js';
import { Modal, Button } from '..//index.js';
import NewPause from '../NewPause.js';
import CasePauseActions from './CasePauseActions.js';
import TaskButtons from './TaskButtons.js';
import UpdateTypeLabel from '../UpdateTypeLabel.js';
import { APIBASE } from '../../config.js';
import ActiveUpdate from './ActiveUpdate.js';
import { uniqueBy } from '@debbie/common/dist/util/index.js';
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useInteractionsByReference } from '@debbie/cortex/dist/interactions/index.js';
export const getActivePauses = (updates, updateTypesMap, meRole) => {
    if (!updates || !meRole || !updateTypesMap)
        return [];
    const activePauses = [];
    updates.forEach((update) => {
        if (update.continuedAt && !moment(update.continuedAt).isAfter(moment())) {
            return;
        }
        const updateType = updateTypesMap[update.updateTypeId];
        if (!updateType)
            return;
        const hasPermission = !!updateType.responsiblePermissionLevel &&
            (updateType.responsiblePermissionLevel === meRole ||
                (updateType.responsiblePermissionLevel ===
                    PermissionLevels.caseworker &&
                    meRole === PermissionLevels.admin));
        const canContinueCase = hasPermission && isCaseworker(meRole);
        const isHidden = !!updateType.hiddenPermissionLevel &&
            updateType.hiddenPermissionLevel === meRole;
        if (!isHidden) {
            activePauses.push({
                update,
                updateType,
                canContinueCase,
                isAwaitingClient: updateType.updateTypeId === UpdateType.AWAITINGCLIENTANSWER,
                futureReminder: moment(update.remindedAt).isBefore(moment())
                    ? null
                    : moment(update.remindedAt),
                futurePause: moment(update.pausedAt).isBefore(moment())
                    ? null
                    : moment(update.pausedAt),
            });
        }
    });
    return activePauses.sort((a, b) => moment(b.update.createdAt).toDate().getTime() -
        moment(a.update.createdAt).toDate().getTime());
};
const CasePauseStatus = (props) => {
    const updateTypesMap = useSelector(selectUpdateTypesMap);
    const customer = useSelector((s) => selectCustomerById(s, props.case.customerId));
    const meRole = useSelector(selectRole);
    const caseUpdates = useSelector((s) => props.case && selectCaseUpdates(s, props.case.caseId));
    const caseVouchers_ = useCaseVouchersByCase(props.case.caseId);
    const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null;
    const workflows = useSelector(selectWorkflows);
    const workflowsIsLoading = useSelector(selectWorkflowsIsLoading);
    const documentsLoadable = useDocumentsByReference(props.case.caseId);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const interactionsLoadable = useInteractionsByReference(Entity.Case, props.case.caseId);
    const interactions = h.hasData(interactionsLoadable)
        ? interactionsLoadable.data
        : null;
    const hasFoundation = documents && !!documents.find((document) => isDocumentFoundation(document));
    const foundationTypes = documents
        ? uniqueBy(getFoundations(documents), (d) => d.type).map((d) => d.type)
        : [];
    const [deletedUpdates, setDeletedUpdates] = useState(new Set());
    useEffect(() => {
        if (!workflows && !workflowsIsLoading)
            c.workflows.getAll();
    });
    const activePauseUpdates = getActivePauses(caseUpdates || [], updateTypesMap, meRole);
    const displayPauses = [];
    activePauseUpdates.forEach((pause) => {
        const { update } = pause;
        if (deletedUpdates.has(update.updateId)) {
            return;
        }
        if (update.update) {
            displayPauses.push({
                shouldBeStacked: false,
                pauses: [pause],
                updateTypeId: update.updateTypeId,
            });
            return;
        }
        const existingPauseStack = displayPauses.find((p) => p.updateTypeId === update.updateTypeId && p.shouldBeStacked);
        if (existingPauseStack) {
            displayPauses[displayPauses.indexOf(existingPauseStack)].pauses.push(pause);
            return;
        }
        displayPauses.push({
            shouldBeStacked: true,
            pauses: [pause],
            updateTypeId: update.updateTypeId,
        });
    });
    const toggleShowNewUpdate = () => props.setShowNewUpdate && props.setShowNewUpdate(!props.showNewUpdate);
    const workflow = workflows &&
        props.case.workflowId &&
        workflows.find((workflow) => workflow.workflowId === props.case.workflowId);
    const nextTask = workflow &&
        caseUpdates &&
        props.case &&
        caseVouchers &&
        customer &&
        interactions &&
        typeof hasFoundation === 'boolean'
        ? getNextTask(caseUpdates, workflow, {
            contact: convertUserToContact(getContactPerson(customer)),
            isCompany: props.case.isCompany,
            updates: caseUpdates,
            caseVouchers: caseVouchers,
            caseGroupId: props.case.caseGroup?.id || null,
            caseId: props.case.caseId,
            creditorId: props.case.creditorId,
            hasFoundation,
            foundationTypes,
            customerTags: customer.tags,
            interactions: interactions,
        })
        : undefined;
    return (React.createElement("div", null,
        props.showNewUpdate && props.case && (React.createElement(NewPause, { toggleNewUpdate: () => toggleShowNewUpdate(), case: props.case })),
        nextTask &&
            //@ts-ignore
            nextTask.manual &&
            //@ts-ignore
            nextTask.tasks &&
            !nextTask.pastUpdate &&
            //@ts-ignore
            !!nextTask.tasks.length && (React.createElement(ActiveUpdate
        //@ts-ignore
        , { 
            //@ts-ignore
            actions: React.createElement(TaskButtons, { case: props.case, tasks: nextTask.tasks }), label: 'Vælg næste opgave' })),
        displayPauses.map((displayPause) => {
            return (React.createElement(ShowPause, { key: displayPause.pauses[0].update.updateId, onDeleteUpdate: (id, deleting) => {
                    setDeletedUpdates((deletedUpdates) => {
                        if (deleting) {
                            deletedUpdates.add(id);
                        }
                        else {
                            deletedUpdates.delete(id);
                        }
                        return new Set(deletedUpdates);
                    });
                }, displayPause: displayPause, singleCase: props.case, nextTask: nextTask }));
        })));
};
export default CasePauseStatus;
const ShowPause = (props) => {
    const { futureReminder, update, futurePause, updateType, canContinueCase, isAwaitingClient, } = props.displayPause.pauses[0];
    const { shouldBeStacked, pauses } = props.displayPause;
    const { nextTask } = props;
    const [deletingUpdate, setDeletingUpdate] = useState(false);
    useEffect(() => {
        setDeletingUpdate(false);
    }, [props.displayPause]);
    if (deletingUpdate) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        updateType && React.createElement(IntroduceUpdateType, { updateType: updateType }),
        React.createElement(ActiveUpdate, { actions: React.createElement(CasePauseActions, { singleCase: props.singleCase, nextTask: nextTask, futurePause: futurePause, update: update, updateType: updateType, canContinueCase: canContinueCase, isAwaitingClient: isAwaitingClient, stack: pauses, setDeletingUpdate: (update, deleting) => {
                    props.onDeleteUpdate(update, deleting);
                } }), futurePause: futurePause, futureReminder: futureReminder, quantity: shouldBeStacked ? pauses.length : 1, updateTypeId: updateType.updateTypeId, update: update })));
};
const introducedKey = (updateType) => `update-type-introduced-${updateType.updateTypeId}`;
const IntroduceUpdateType = (props) => {
    const { updateType } = props;
    const [introduced, _setIntroduced] = useState(!!localStorage.getItem(introducedKey(updateType)));
    const setIntroduced = (s) => {
        localStorage.setItem(introducedKey(updateType), s ? 'true' : '');
        _setIntroduced(s);
    };
    if (introduced || !updateType.introductionHTML) {
        return null;
    }
    return (React.createElement(Modal, { header: React.createElement(UpdateTypeLabel, { updateTypeId: updateType.updateTypeId, label: updateType.label }), onToggle: () => setIntroduced(true), maxWidth: "600px" },
        updateType.introductionHeroFile?.fileId && (React.createElement("img", { src: `${APIBASE}/files/${updateType.introductionHeroFile.fileId}/${updateType.introductionHeroFile.fileName}` })),
        React.createElement("div", { dangerouslySetInnerHTML: {
                __html: updateType.introductionHTML,
            } }),
        React.createElement("br", null),
        React.createElement("br", null),
        React.createElement(Button, { block: true, onClick: () => setIntroduced(true) }, "Ok")));
};
//# sourceMappingURL=CasePauseStatus.js.map